import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RegExp } from 'src/app/core/config/regex';
import { Context, Role } from 'src/app/shared/models/context';
import { EmployeeData, EmployeeProfileData, Image, Medium, Message } from 'src/app/shared/models/domain';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { CompanyService } from 'src/app/shared/services/company.service';
import { ContextService } from 'src/app/shared/services/context.service';
import { DomainService } from 'src/app/shared/services/domain.service';
import { ProfileService } from 'src/app/shared/services/profile.service';
import { SettingsService } from 'src/app/shared/services/settings.service';

@Component({
  selector: 'app-employee-profile',
  templateUrl: './employee-profile.component.html',
  styleUrls: ['./employee-profile.component.scss']
})
export class EmployeeProfileComponent implements OnInit {
  @Input() employee: EmployeeProfileData;
  @Output() passEntry: EventEmitter<any> = new EventEmitter();
  public roles: Role[] = [];
  public submitted: boolean = false;
  public genders: string[] = [];
  public bloods: string[] = [];
  public surnames: string[] = [];
  public profileForm: FormGroup;
  public modified: boolean = false;
  public reports: EmployeeData[] = [];
  public profile: EmployeeProfileData;
  public context: Context;
  public onLoad: boolean = false;
  private imageUrl: string;
  public image: string;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private contextService: ContextService,
    private settingsService: SettingsService,
    private profileService: ProfileService,
    private companyService: CompanyService,
    private commonService: CommonService,
    private domainService: DomainService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.surnames = this.commonService.getSurnames();
    this.contextService.getGenders().subscribe((genders) => (this.genders = genders));
    this.contextService.getBloodGroups().subscribe((bloods) => (this.bloods = bloods));
    this.context = this.authService.getContextData();
    this.profileService.getEmployeeProfile().subscribe((profile: EmployeeProfileData) => {
      this.profile = profile;
    });
    if (this.context.companyId) {
      this.companyService.getEmployees(this.context.companyId).subscribe((report: EmployeeData[]) => (this.reports = report));
      this.settingsService.getRoles(this.context.companyId).subscribe((roles: Role[]) => this.roles = roles.filter(r => r.persona !== 'CLIENT'));
    }
    this.getImage();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['employee']) {
      this.employee = changes['employee'].currentValue;
      this.setProfile(this.employee);
    }
  }

  private setProfile(employee?: EmployeeProfileData) {
    if (!employee)
      employee = {
        employeeData: {
          id: null,
          name: null,
          surname: null,
          code: null,
          dob: null,
          doj: null,
          gender: null,
          email: null,
          aadhaar: null,
          pan: null,
          avatar: null,
          title: 'Mr',
          blood: null,
          addressId: null,
          userId: null,
          reportTo: null,
          addressTo: null,
          birthday: null,
          salaried: true,
          disabled: false,
          employeeType: null,
          employeeStatus: null,
        },
        userData: {
          id: null,
          patronId: null,
          username: null,
          roleName: null,
          password: null,
          roleId: null,
          session: null,
          client: null,
          timestamp: null,
          verified: true,
          disabled: false,
          companyId: this.context?.companyId,
          confirm: '',
        },
        medium: [],
      } as EmployeeProfileData;
    this.profileForm = this.fb.group({
      employeeData: this.fb.group({
        id: this.fb.control(employee?.employeeData?.id, []),
        name: this.fb.control(employee?.employeeData?.name, []),
        surname: this.fb.control(employee?.employeeData?.surname, []),
        code: this.fb.control(employee?.employeeData?.code, []),
        dob: this.fb.control(employee?.employeeData?.dob, []),
        doj: this.fb.control(employee?.employeeData?.doj, []),
        gender: this.fb.control(employee?.employeeData?.gender, []),
        email: this.fb.control(employee?.employeeData?.email, []),
        aadhaar: this.fb.control(employee?.employeeData?.aadhaar, []),
        pan: this.fb.control(employee?.employeeData?.pan, []),
        avatar: this.fb.control(employee?.employeeData?.avatar, []),
        title: this.fb.control(employee?.employeeData?.title, []),
        blood: this.fb.control(employee?.employeeData?.blood, []),
        addressId: this.fb.control(employee?.employeeData?.addressId, []),
        userId: this.fb.control(employee?.employeeData?.userId, []),
        reportTo: this.fb.control(employee?.employeeData?.reportTo, []),
        addressTo: this.fb.control(employee?.employeeData?.addressTo, []),
        salaried: this.fb.control(employee?.employeeData?.salaried, []),
        disabled: this.fb.control(employee?.employeeData?.disabled, []),
      }),
      userData: this.fb.group({
        id: this.fb.control(employee?.userData?.id, [Validators.required]),
        patronId: this.fb.control(employee?.userData?.patronId || null, []),
        username: this.fb.control(employee?.userData?.username, [Validators.required]),
        password: this.fb.control(employee?.userData?.password, [!employee?.userData?.id ? Validators.required : Validators.nullValidator, Validators.pattern(RegExp.password)]),
        roleId: this.fb.control(employee?.userData?.roleId, [Validators.required]),
        roleName: this.fb.control(employee?.userData?.roleName, []),
        session: this.fb.control(employee?.userData?.session, []),
        client: this.fb.control(employee?.userData?.client, []),
        timestamp: this.fb.control(employee?.userData?.timestamp, []),
        companyId: this.fb.control(employee?.userData?.companyId, [Validators.required]),
        verified: this.fb.control(employee?.userData?.verified, []),
        disabled: this.fb.control(employee?.userData?.disabled, []),
      }),

    }),
      this.profileForm.valueChanges.subscribe(value => this.modified = true);
  }
  get e() {
    return this.profileForm.controls;
  }

  get u() {
    return this.e.employeeData as FormGroup;
  }

  get a() {
    return this.e.userData as FormGroup;
  }

  public async getAndSetBase64(event: any) {
    const image: string = await this.commonService.convertImgToBase64(event);
    if (!image) return;
    this.imageUrl = image;
    this.domainService.setEmployeeImage(image);
    // this.saveImage(this.employee.employeeData.id, 'EMPLOYEE', 'avatar', image);
  }

  private saveImage(sid: number, refType: string, label: string, url: string) {
    let image: Image = {
      id: null,
      sid: sid,
      type: refType,
      label: label,
      image: url,
    };
    image.image = url
    this.domainService.createImage(image).subscribe({
      next: (response: string) => {
        const uploadedImageUrl = response; 
        this.domainService.setEmployeeImage(uploadedImageUrl);
        this.cdr.detectChanges();
      },
      error: (error: HttpErrorResponse) => {
      }
    });
  }

  private getImage() {
    this.domainService
      .getImage('EMPLOYEE', 'EMPLOYEE', this.employee.employeeData.id, 'avatar')
      .subscribe(
        (image: Message) => {
          this.image=image.payload;
          this.domainService.setEmployeeImage(image.payload); 
          this.cdr.detectChanges();
          this.setProfile(this.employee);
        },
        (error) => {
          this.image = null; 
        }
      );
  }


  public upsertEmployeeProfile(inProgress?: any) {
    this.submitted = true;
    if (this.profileForm.valid) {
      let employee: EmployeeProfileData = this.profileForm.value;
      if (!this.modified) {
        this.commonService.setToastr(200, `Employee ${employee.employeeData.name} has been updated successfully!`);
        return;
      }
      inProgress = true;

      if (employee.employeeData.id) {
        this.profileService.upsertEmployeeProfile(employee.employeeData.id, employee).subscribe({
          next: (response: Message) => {
            inProgress = false;
            if (!response.failed) {
              if(this.imageUrl){
                this.saveImage(employee.employeeData.id, 'EMPLOYEE', 'avatar', this.imageUrl);
              }
              this.getImage();
              this.passEntry.emit(true);
              this.onLoad = true;
              this.commonService.setToastr(200, `Employee ${employee.employeeData.name} has been updated successfully!`);
            } else {
              this.commonService.setToastr(500, `Cannot update the record. Employee '${employee.employeeData.name}' already exists.`);
            }
            this.submitted = false;
          },
          error: (error: HttpErrorResponse) => {
            inProgress = false;
            this.commonService.setToastr(500, error?.error?.name || error.error);
          },
        });
      }
    }
  }
}

